import type { ResultOf } from "@graphql-typed-document-node/core";
import { createContext, type JSX, type ReactNode, useContext } from "react";
import { useQuery } from "urql";
import Loader from "../../shared/components/Loader";
import { graphql } from "../gql";
import ShowError from "./ShowError";

const query = graphql(`
    query AppCurrentPersonQuery {
        currentPerson {
            id
            firstName
            lastName
            profilePicSrc
            user {
                role {
                    name
                }
                pulseRights
                agreedToTerms
                requireNewPassword
                firstTimeLogin
            }
            ongoingPaths: learningPaths(filter: "ongoing") {
                id
                pathname
            }
            currentPath {
                id
                pathname
            }
            organisation {
                id
                name
            }
            teamScans {
                id
            }
            standaloneAiChatbots {
                id
            }
        }
    }
`);

export type ICurrentPerson = ResultOf<typeof query>["currentPerson"];

const CurrentPersonContext = createContext<ICurrentPerson>(null);
CurrentPersonContext.displayName = "CurrentPersonContext";

interface IProps {
    children: ReactNode;
}

export function CurrentPersonProvider({ children }: IProps): JSX.Element {
    const [result] = useQuery({ query });

    if (result.fetching) {
        return <Loader />;
    }
    if (result.error) {
        return <ShowError />;
    }
    return <CurrentPersonContext.Provider value={result.data.currentPerson}>{children}</CurrentPersonContext.Provider>;
}

export function useCurrentPerson(): ICurrentPerson {
    return useContext(CurrentPersonContext);
}
